import React from "react";
import { Link } from "react-router-dom";
import restaurantImg from "../assets/restorent/restaurant.png";

const valuesStats = [
  { num: "25+", line1: "Years Of", line2: "Experience" },
  { num: "650+", line1: "Outdoor", line2: "Catering" },
  { num: "82+", line1: "Special", line2: "Dishes" },
  { num: "35+", line1: "Master", line2: "Chefs" },
];

const AboutSection = () => {
  return (
    <section id="about-us" className="relative py-28 bg-white overflow-hidden">
      {/* ── Background Royal Ornaments & Glows ── */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-radial from-[#D4AF37]/15 via-transparent to-transparent blur-3xl rounded-full" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-[#9b1c2e]/5 blur-3xl rounded-full" />
        <div className="absolute bottom-10 -right-40 w-96 h-96 bg-[#D4AF37]/10 blur-3xl rounded-full" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 lg:px-8">

        {/* ── Header Section ── */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-14">
          {/* Sub-badge with Crown */}
          <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-[#9b1c2e]/8 border border-[#D4AF37]/40 shadow-sm">

            <span
              className="text-[11px] font-bold tracking-[0.35em] uppercase text-[#9b1c2e]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Discover Our Story
            </span>

          </div>

          {/* Main Title */}
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight uppercase tracking-wider"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            About <span className="bg-gradient-to-r from-[#9b1c2e] via-[#c4253c] to-[#9b1c2e] bg-clip-text text-transparent">Barbequeen</span> Restaurant
          </h2>

          {/* Royal Crest SVG Divider */}
          <div className="flex items-center justify-center gap-4 py-1">
            <div className="h-[1px] w-16 bg-gradient-to-r from-transparent via-[#D4AF37] to-[#D4AF37]" />
            <div className="text-[#D4AF37] text-lg select-none">⚜</div>
            <div className="h-[1px] w-16 bg-gradient-to-l from-transparent via-[#D4AF37] to-[#D4AF37]" />
          </div>
        </div>

        {/* ── 2-Column Main Showcase Grid (Image on Left, Text & Buttons on Right) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center my-6">

          {/* Left Column (6 cols): Imperial Photo Showcase */}
          <div className="lg:col-span-6 relative flex justify-center">
            {/* Outer Decorative Ring & Glow */}
            <div className="absolute inset-0 w-full h-full rounded-full border border-[#D4AF37]/30 -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block animate-pulse" />

            {/* Image Outer Imperial Border Frame */}
            <div className="relative p-3 rounded-3xl bg-gradient-to-b from-[#FAF0D7] via-[#FFFDF9] to-[#E6C594] shadow-[0_15px_40px_rgba(155,28,46,0.12)] border border-[#D4AF37]/50 w-full group">

              {/* Corner Ornaments */}
              <div className="absolute top-2 left-2 text-[#D4AF37] z-20 text-xs select-none">⚜</div>
              <div className="absolute top-2 right-2 text-[#D4AF37] z-20 text-xs select-none">⚜</div>
              <div className="absolute bottom-2 left-2 text-[#D4AF37] z-20 text-xs select-none">⚜</div>
              <div className="absolute bottom-2 right-2 text-[#D4AF37] z-20 text-xs select-none">⚜</div>

              {/* Image Container */}
              <div className="relative rounded-2xl overflow-hidden bg-gray-900 aspect-[4/3] w-full">
                <img
                  src={restaurantImg}
                  alt="Barbequeen Royal Dining"
                  className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=800&auto=format&fit=crop";
                  }}
                />

                {/* Dark Vignette Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />

                {/* Royal Seal Badge */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between px-4 py-2.5 rounded-xl bg-black/60 backdrop-blur-md border border-[#D4AF37]/40">
                  <div className="flex items-center gap-2">
                    <span className="text-[#D4AF37] text-sm">👑</span>
                    <span className="text-xs font-bold tracking-widest text-[#F3E5AB] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Est. Royal Dining Since 1999
                    </span>
                  </div>
                  <span className="text-xs text-gray-300 font-serif italic hidden sm:inline">
                    Authentic BBQ Heritage
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* Right Column (6 cols): Paragraph Text + Action Buttons */}
          <div className="lg:col-span-6 space-y-6 text-left">
            <div className="space-y-4">
              <h3
                className="text-2xl sm:text-3xl font-bold text-gray-900 font-serif tracking-wide"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                A Legacy of Imperial Flavours
              </h3>

              <p className="text-gray-800 text-base sm:text-lg leading-relaxed font-serif italic">
                &ldquo;Where authentic royal flavours meet timeless culinary heritage. Craving exquisite grilled master delicacies or aromatic biryani crafted with imperial spices? Experience taste redefined.&rdquo;
              </p>

              <p className="text-gray-600 text-sm leading-relaxed font-sans">
                An expert in Grill, barbeque-style, tandoori, Indian & Chinese Cuisine since 1999, Barbequeen Restaurant has been the go-to destination for family and food lovers seeking uncompromising taste and royal hospitality.
              </p>
            </div>

            {/* Royal Action Bar Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch pt-2">

              {/* Royal Phone Call CTA */}
              <a
                href="tel:+919597228788"
                className="flex items-center gap-3 px-5 py-3.5 bg-gradient-to-br from-white via-[#FFFDF9] to-[#FBF6ED] border-2 border-[#D4AF37]/60 hover:border-[#D4AF37] rounded-2xl transition-all duration-300 shadow-md hover:shadow-xl shadow-[#D4AF37]/10 group hover:-translate-y-0.5"
              >
                <div className="w-10 h-10 shrink-0 rounded-xl bg-[#9b1c2e] border border-[#D4AF37]/50 flex items-center justify-center text-[#F3E5AB] shadow-md group-hover:scale-105 transition-transform">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="text-left min-w-0">
                  <p className="text-[10px] text-[#9b1c2e] font-bold uppercase tracking-widest truncate" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Book Through Call
                  </p>
                  <p className="text-xs sm:text-sm font-black text-gray-900 font-serif tracking-tight truncate">
                    +91-95972-28788
                  </p>
                </div>
              </a>

              {/* Learn More Button */}
              <Link
                to="/about-us"
                className="flex items-center gap-3 px-5 py-3.5 bg-gradient-to-r from-[#9b1c2e] via-[#801220] to-[#590b14] border-2 border-[#D4AF37]/60 hover:border-[#D4AF37] rounded-2xl transition-all duration-300 shadow-xl shadow-[#9b1c2e]/20 hover:shadow-[#D4AF37]/30 group hover:-translate-y-0.5"
              >
                <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-[#D4AF37] via-[#F3E5AB] to-[#B38728] border border-white/40 flex items-center justify-center text-[#590b14] shadow-md group-hover:scale-105 transition-transform">
                  <span className="text-base select-none">⚜</span>
                </div>
                <div className="text-left min-w-0">
                  <p className="text-[10px] text-[#F3E5AB] font-bold uppercase tracking-widest truncate" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Our Royal Heritage
                  </p>
                  <p className="text-xs sm:text-sm font-bold text-white uppercase tracking-wider truncate" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Learn More
                  </p>
                </div>
              </Link>

            </div>
          </div>

        </div>

      </div>

      {/* ── Values / Stats Full-Width Horizontal Band ── */}
      <div className="w-full mt-14 py-8 px-4 sm:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
          {valuesStats.map((stat, idx) => (
            <div key={idx} className="relative flex items-center justify-center p-3 sm:p-4 group">
              {/* Giant Watermark Serif Number */}
              <span
                className="text-6xl sm:text-7xl font-serif font-bold text-[#EAE0D2] group-hover:text-[#E2D2BC] transition-colors select-none leading-none tracking-tighter"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {stat.num}
              </span>

              {/* Overlapping Label */}
              <div className="ml-[-1.25rem] sm:ml-[-1.75rem] z-10 text-left font-serif leading-tight">
                <p
                  className="text-xs sm:text-sm font-bold text-gray-800 tracking-wide font-serif"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {stat.line1}
                </p>
                <p
                  className="text-xs sm:text-sm font-bold text-gray-800 tracking-wide font-serif"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {stat.line2}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
